import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  TextField,
  Divider,
  Avatar,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Send as SendIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import TimezoneUtils from '../utils/timezoneUtils';
import { announcementService } from '../services/announcementService';
import { useAuth } from '../context/AuthContext';
import ImageSlideshow from './ImageSlideshow';

const formatDate = (date) => {
  if (!date) return '';
  try {
    return TimezoneUtils.formatDateForDisplay(new Date(date), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const formatAudience = (audience) => {
  if (!audience || audience.all) {
    return 'All users';
  }

  const parts = [];
  if (audience.roles?.length) {
    parts.push(audience.roles.join(', '));
  }
  if (audience.teamIds?.length) {
    const teamNames = audience.teamIds.map((team) => team.name || 'Team').join(', ');
    parts.push(teamNames);
  }
  return parts.join(' · ') || 'All users';
};

const AnnouncementDetailDialog = ({ open, onClose, announcementId, onRead, onVoteUpdate }) => {
  const { user } = useAuth();
  const isBoss = user?.role === 'boss';
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [voting, setVoting] = useState(false);

  const fetchAnnouncement = async () => {
    setLoading(true);
    try {
      const data = await announcementService.getAnnouncement(announcementId);
      setAnnouncement(data);

      if (!data.isRead) {
        await announcementService.markAsRead(announcementId);
        onRead?.();
      }
    } catch (error) {
      toast.error('Failed to load announcement');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !announcementId) {
      setAnnouncement(null);
      setReplyText('');
      return;
    }

    fetchAnnouncement();
  }, [open, announcementId]);

  const handleVote = async (action) => {
    if (!announcement || voting) return;

    setVoting(true);
    try {
      const result = await announcementService.vote(announcementId, action);
      setAnnouncement((prev) => ({
        ...prev,
        upvotes: result.upvotes,
        downvotes: result.downvotes,
        userVote: result.userVote
      }));
      onVoteUpdate?.({
        upvotes: result.upvotes,
        downvotes: result.downvotes
      });
    } catch (error) {
      toast.error('Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || submittingReply) return;

    setSubmittingReply(true);
    try {
      const result = await announcementService.addComment(announcementId, replyText.trim());
      setAnnouncement((prev) => ({
        ...prev,
        comments: [...(prev.comments || []), result.comment],
        commentCount: result.commentCount
      }));
      setReplyText('');
      toast.success('Reply posted');
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const attachmentImages = useMemo(() => {
    if (!announcement?.attachments?.length) return [];
    return announcement.attachments.map((attachment, index) => ({
      src: `${process.env.REACT_APP_API_URL}/uploads/${attachment.path}`,
      alt: attachment.filename || `Screenshot ${index + 1}`
    }));
  }, [announcement?.attachments]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={attachmentImages.length > 0 ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  flexShrink: 0,
                  mt: 0.25
                }}
              >
                <CampaignIcon color="primary" fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="div" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                  {announcement?.title || 'Announcement'}
                </Typography>
                {announcement && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {formatDate(announcement.publishedAt)}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, mr: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={32} />
            </Box>
          ) : announcement ? (
            <Stack spacing={2.5}>
              {isBoss && (
                <Chip
                  label={formatAudience(announcement.audience)}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
              )}

              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                  color: 'text.primary'
                }}
              >
                {announcement.body}
              </Typography>

              {attachmentImages.length > 0 && (
                <ImageSlideshow images={attachmentImages} maxHeight={400} enableKeyboard={open} />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant={announcement.userVote === 'up' ? 'contained' : 'outlined'}
                  startIcon={<ThumbUpIcon />}
                  onClick={() => handleVote('upvote')}
                  disabled={voting}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {announcement.upvotes || 0}
                </Button>
                <Button
                  size="small"
                  variant={announcement.userVote === 'down' ? 'contained' : 'outlined'}
                  color={announcement.userVote === 'down' ? 'error' : 'inherit'}
                  startIcon={<ThumbDownIcon />}
                  onClick={() => handleVote('downvote')}
                  disabled={voting}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {announcement.downvotes || 0}
                </Button>
              </Box>

              <Divider />

              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                Replies ({announcement.comments?.length || 0})
              </Typography>

              {(announcement.comments || []).length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                  No replies yet. Be the first to respond.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {(announcement.comments || []).map((comment) => (
                    <Box
                      key={comment._id}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'action.hover'
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                        {(comment.author?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {comment.author?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatDate(comment.created_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, lineHeight: 1.5 }}>
                          {comment.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  disabled={submittingReply}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSubmitReply}
                  disabled={submittingReply || !replyText.trim()}
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2) }
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
    </Dialog>
  );
};

export default AnnouncementDetailDialog;
